// Friends accept view - processes friend invitation codes

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// UserCheck icon SVG
const userCheckIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="64"
  height="64"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  <circle cx="9" cy="7" r="4" />
  <polyline points="16 11 18 13 22 9" />
</svg>`;

// UserX icon SVG
const userXIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="64"
  height="64"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
>
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
  <circle cx="9" cy="7" r="4" />
  <line x1="17" x2="22" y1="8" y2="13" />
  <line x1="22" x2="17" y1="8" y2="13" />
</svg>`;

/**
 * Friends accept view component
 * Processes friend invitation codes and adds the friend relationship
 */
class FriendsAcceptView extends BaseComponent {
  static properties = {
    code: { type: String },
    _loading: { type: Boolean, state: true },
    _success: { type: Boolean, state: true },
    _errorMessage: { type: String, state: true },
    _friendInfo: { type: Object, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .friends-accept-view {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
      background-color: #f8f9fa;
    }
  `;

  constructor() {
    super();
    this.code = '';
    this._loading = true;
    this._success = false;
    this._errorMessage = null;
    this._friendInfo = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._acceptInvite();
  }

  /** @param {string} emailHash */
  _getGravatarUrl(emailHash) {
    return `https://www.gravatar.com/avatar/${emailHash}?d=identicon&s=128`;
  }

  async _acceptInvite() {
    this._loading = true;
    this._errorMessage = null;

    try {
      const data =
        /** @type {{success: boolean, message?: string, friend?: {email_hash: string, username: string}}} */ (
          await api.acceptFriendInvitation(this.code)
        );

      if (data.success) {
        this._success = true;
        this._friendInfo = data.friend;
        // Redirect to friends page after a short delay
        setTimeout(() => {
          router.navigate('/friends');
        }, 2000);
      } else {
        this._errorMessage = data.message || 'Failed to accept friend invite';
      }
    } catch (err) {
      console.error('Failed to accept friend invite:', err);
      this._errorMessage = 'An error occurred. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  _goToFriends() {
    router.navigate('/friends');
  }

  render() {
    return html`
      <div class="friends-accept-view">
        <div class="card" style="max-width: 400px; width: 100%;">
          <div class="card-body text-center">
            ${
              this._loading
                ? html`
                  <div class="spinner-border text-primary mb-3" role="status">
                    <span class="visually-hidden">Processing...</span>
                  </div>
                  <h5 class="card-title">Processing Friend Request</h5>
                  <p class="text-muted">Please wait...</p>
                `
                : this._success
                  ? html`
                    <div class="text-success mb-3">${userCheckIcon}</div>
                    <h5 class="card-title">You're Now Friends!</h5>
                    ${
                      this._friendInfo
                        ? html`
                          <div class="mb-3">
                            <img
                              src="${this._getGravatarUrl(this._friendInfo.email_hash)}"
                              alt="${this._friendInfo.username}"
                              class="rounded-circle mb-2"
                              style="width: 64px; height: 64px;"
                            />
                            <p class="mb-0 fw-semibold">${this._friendInfo.username}</p>
                          </div>
                        `
                        : ''
                    }
                    <p class="text-muted">Redirecting to your friends list...</p>
                  `
                  : this._errorMessage
                    ? html`
                      <div class="text-danger mb-3">${userXIcon}</div>
                      <h5 class="card-title">Unable to Add Friend</h5>
                      <p class="text-muted mb-3">${this._errorMessage}</p>
                      <button class="btn btn-primary" @click="${this._goToFriends}">Go to Friends</button>
                    `
                    : ''
            }
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('friends-accept-view', FriendsAcceptView);
